import { randomId } from '@mantine/hooks';
import { RichTextEditor } from '@mantine/tiptap';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useState } from 'react';

export type Controlled_MarkdownProps = {
    value: string;
    onChange?: (value: string) => void;
    readonly: boolean;
};

export const Controlled_Markdown = ({
    value,
    onChange,
    readonly,
}: Controlled_MarkdownProps) => {
    const [content, setContent] = useState(value);
    const [updateKey, setUpdateKey] = useState('updateKey');

    useEffect(() => {
        if (content !== value) {
            setContent(value);
            setUpdateKey(randomId());
        }
    }, [content, value]);

    return (
        <InternalControlled_Markdown
            key={updateKey}
            value={content}
            readonly={readonly}
            onChange={(newValue) => {
                setContent(newValue);
                if (onChange) {
                    onChange(newValue);
                }
            }}
        />
    );
};

const InternalControlled_Markdown = ({
    value,
    onChange,
    readonly,
}: Controlled_MarkdownProps) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({ placeholder: 'not defined' }),
        ],
        content: value,
        onUpdate({ editor }) {
            if (onChange) {
                // onChange(editor.storage.markdown.getMarkdown();
                onChange(editor.getHTML());
                editor.chain().focus();
            }
        },
        editable: !readonly,
        shouldRerenderOnTransaction: true,
    });

    if (readonly) {
        return (
            <RichTextEditor
                editor={editor}
                w={'100%'}
                style={{ textAlign: 'left' }}
            >
                <RichTextEditor.Content />
            </RichTextEditor>
        );
    }

    return (
        <RichTextEditor
            editor={editor}
            w={'100%'}
            style={{ textAlign: 'left' }}
        >
            <RichTextEditor.Toolbar
                sticky
                stickyOffset={60}
            >
                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Strikethrough />
                    <RichTextEditor.ClearFormatting />
                    <RichTextEditor.Code />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.H1 />
                    <RichTextEditor.H2 />
                    <RichTextEditor.H3 />
                    <RichTextEditor.H4 />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Blockquote />
                    <RichTextEditor.Hr />
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Link />
                    <RichTextEditor.Unlink />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.AlignLeft />
                    <RichTextEditor.AlignCenter />
                    <RichTextEditor.AlignJustify />
                    <RichTextEditor.AlignRight />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Undo />
                    <RichTextEditor.Redo />
                </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>

            <RichTextEditor.Content />
        </RichTextEditor>
    );
};
